/**
 * Modal Portal Component
 * Renders children into a global modal container using React Portal
 */

import { useRef, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';

interface ModalPortalProps {
  children: React.ReactNode;
  onClose?: () => void; // accepted for API compatibility; actual close logic handled by children
}

export const ModalPortal: React.FC<ModalPortalProps> = ({ children }) => {
  // Create the element once and keep it stable — never nulled out.
  const elRef = useRef<HTMLDivElement | null>(null);
  if (!elRef.current) {
    const el = document.createElement('div');
    el.style.pointerEvents = 'auto';
    elRef.current = el;
  }

  // useLayoutEffect (not useEffect) so Strict Mode's cleanup→remount cycle
  // runs synchronously before paint, preventing the two-click issue.
  useLayoutEffect(() => {
    let modalRoot = document.getElementById('modal-root');
    if (!modalRoot) {
      modalRoot = document.createElement('div');
      modalRoot.id = 'modal-root';
      modalRoot.style.position = 'fixed';
      modalRoot.style.top = '0';
      modalRoot.style.left = '0';
      modalRoot.style.width = '100%';
      modalRoot.style.height = '100%';
      modalRoot.style.pointerEvents = 'none';
      modalRoot.style.zIndex = '999999';
      document.body.appendChild(modalRoot);
    }
    modalRoot.appendChild(elRef.current!);
    return () => {
      const el = elRef.current;
      if (el?.parentNode) {
        el.parentNode.removeChild(el);
      }
    };
  }, []);

  return createPortal(children, elRef.current);
};
